import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft, Plus, Pill, Stethoscope, Edit2, Trash2, Bell, BellOff,
  Sparkles, Check, Circle, CalendarCheck, Flame,
} from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/EmptyState'
import { useMedications } from '../../hooks/useMedications'
import { useSupplements } from '../../hooks/useSupplements'
import { useHealthConditions } from '../../hooks/useHealthConditions'
import { useIntakeLog } from '../../hooks/useIntakeLog'
import { useIntakeHistory } from '../../hooks/useIntakeHistory'
import { useConfirm } from '../../contexts/ConfirmDialogContext'
import { computeAdherenceStreak } from '../../lib/adherenceStreak'
import { performRefresh } from '../../lib/refresh'
import { VitalsSection } from './components/VitalsSection'
import PullToRefresh from '../../components/PullToRefresh'
import { cn } from '../../lib/utils'

const FREQUENCIES = ['daily', 'twice_daily', 'three_daily', 'weekly', 'as_needed']

// "HH:MM" 24-hour normalizer — mirrors the server-side helper in the
// send-reminders edge function so the times the form saves are the
// exact strings the cron job will compare against.
function normalizeHHMM(s) {
  const m = /^\s*(\d{1,2}):(\d{2})\s*$/.exec(s)
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (h < 0 || h > 23 || min < 0 || min > 59) return null
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
}

// Form shared by Medications and Supplements — they have the same
// fields (name / dosage / schedule / reminders / notes), only the
// translation prefix differs. `kind` is 'medication' or 'supplement'.
function ScheduledItemForm({ initial, onSave, onCancel, submitting, kind }) {
  const { t } = useTranslation()
  const tk = (key) => t(`healthProfile.${kind}.${key}`)
  const [name, setName] = useState(initial?.name || '')
  const [dosage, setDosage] = useState(initial?.dosage || '')
  const [frequency, setFrequency] = useState(initial?.frequency || 'daily')
  const [scheduleTimesText, setScheduleTimesText] = useState(
    (initial?.schedule_times || []).join(', ')
  )
  const [startedAt, setStartedAt] = useState(initial?.started_at || '')
  const [remindersEnabled, setRemindersEnabled] = useState(initial?.reminders_enabled || false)
  const [notes, setNotes] = useState(initial?.notes || '')

  const handleSubmit = (e) => {
    e.preventDefault()
    const times = scheduleTimesText.split(',').map(normalizeHHMM).filter(Boolean)
    onSave({
      name: name.trim(),
      dosage: dosage.trim() || null,
      frequency,
      schedule_times: times,
      started_at: startedAt || null,
      reminders_enabled: remindersEnabled,
      notes: notes.trim() || null,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 p-3 rounded-lg border border-input bg-muted/30">
      <div>
        <label className="block text-xs text-muted-foreground mb-1" dir="auto">{tk('name')}</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={80}
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-muted-foreground mb-1" dir="auto">{tk('dosage')}</label>
          <input
            type="text"
            value={dosage}
            onChange={(e) => setDosage(e.target.value)}
            placeholder={kind === 'supplement' ? '1000 IU' : '500mg'}
            className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1" dir="auto">{tk('frequency')}</label>
          <select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
            dir="auto"
            className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm"
          >
            {FREQUENCIES.map((f) => (
              <option key={f} value={f}>{t(`healthProfile.${kind}.frequencies.${f}`)}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs text-muted-foreground mb-1" dir="auto">{tk('scheduleTimes')}</label>
        <input
          type="text"
          value={scheduleTimesText}
          onChange={(e) => setScheduleTimesText(e.target.value)}
          placeholder="08:00, 20:00"
          dir="ltr"
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono"
        />
        <p className="text-[10px] text-muted-foreground mt-1" dir="auto">{tk('scheduleHint')}</p>
      </div>
      <div>
        <label className="block text-xs text-muted-foreground mb-1" dir="auto">{tk('startedAt')}</label>
        <input
          type="date"
          value={startedAt}
          onChange={(e) => setStartedAt(e.target.value)}
          dir="ltr"
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono"
        />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={remindersEnabled}
          onChange={(e) => setRemindersEnabled(e.target.checked)}
          className="w-4 h-4"
        />
        <span dir="auto">{tk('reminders')}</span>
      </label>
      <div>
        <label className="block text-xs text-muted-foreground mb-1" dir="auto">{tk('notes')}</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm resize-none"
        />
      </div>
      <div className="flex gap-2 pt-1">
        <Button type="submit" disabled={submitting || !name.trim()} className="flex-1">
          {t('common.save')}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}

function ConditionForm({ initial, onSave, onCancel, submitting }) {
  const { t } = useTranslation()
  const [name, setName] = useState(initial?.name || '')
  const [diagnosedAt, setDiagnosedAt] = useState(initial?.diagnosed_at || '')
  const [doctor, setDoctor] = useState(initial?.doctor || '')
  const [isActive, setIsActive] = useState(initial?.is_active ?? true)
  const [notes, setNotes] = useState(initial?.notes || '')

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave({
      name: name.trim(),
      diagnosed_at: diagnosedAt || null,
      doctor: doctor.trim() || null,
      is_active: isActive,
      notes: notes.trim() || null,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 p-3 rounded-lg border border-input bg-muted/30">
      <div>
        <label className="block text-xs text-muted-foreground mb-1" dir="auto">{t('healthProfile.condition.name')}</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={80}
          className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-muted-foreground mb-1" dir="auto">{t('healthProfile.condition.diagnosedAt')}</label>
          <input
            type="date"
            value={diagnosedAt}
            onChange={(e) => setDiagnosedAt(e.target.value)}
            dir="ltr"
            className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm font-mono"
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1" dir="auto">{t('healthProfile.condition.doctor')}</label>
          <input
            type="text"
            value={doctor}
            onChange={(e) => setDoctor(e.target.value)}
            className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm"
          />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          className="w-4 h-4"
        />
        <span dir="auto">{t('healthProfile.condition.active')}</span>
      </label>
      <div>
        <label className="block text-xs text-muted-foreground mb-1" dir="auto">{t('healthProfile.condition.notes')}</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm resize-none"
        />
      </div>
      <div className="flex gap-2 pt-1">
        <Button type="submit" disabled={submitting || !name.trim()} className="flex-1">
          {t('common.save')}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}

// Today's scheduled doses across medications + supplements. Each row
// is a tap target that toggles intake_log. Sorted by time so the
// user sees their day in chronological order.
function TodayDoseChecklist({ medications, supplements, intake }) {
  const { t } = useTranslation()

  const doses = useMemo(() => {
    const out = []
    const push = (kind, items) => {
      for (const item of items) {
        const times = Array.isArray(item.schedule_times) ? item.schedule_times : []
        for (const time of times) {
          const norm = normalizeHHMM(time)
          if (!norm) continue
          out.push({
            key: `${kind}-${item.id}-${norm}`,
            kind,
            id: item.id,
            name: item.name,
            dosage: item.dosage,
            time: norm,
          })
        }
      }
    }
    push('medication', medications)
    push('supplement', supplements)
    out.sort((a, b) => a.time.localeCompare(b.time))
    return out
  }, [medications, supplements])

  if (doses.length === 0) return null

  const totalTaken = doses.filter((d) => intake.isTaken(d.kind, d.id, d.time)).length

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5" dir="auto">
            <CalendarCheck className="w-4 h-4 text-app-primary-500" />
            {t('healthProfile.todayDoses.title')}
          </h2>
          <span className="text-xs text-muted-foreground font-mono">
            {totalTaken} / {doses.length}
          </span>
        </div>
        <ul className="space-y-1">
          {doses.map((d) => {
            const taken = intake.isTaken(d.kind, d.id, d.time)
            return (
              <li key={d.key}>
                <button
                  type="button"
                  onClick={() => intake.toggleDose({ itemKind: d.kind, itemId: d.id, scheduledTime: d.time })}
                  className={cn(
                    'w-full flex items-center gap-3 px-2 py-2 rounded-md text-start transition-colors',
                    taken
                      ? 'bg-app-primary-500/5 hover:bg-app-primary-500/10'
                      : 'hover:bg-accent'
                  )}
                >
                  {taken ? (
                    <Check className="w-5 h-5 text-app-primary-500 flex-shrink-0" />
                  ) : (
                    <Circle className="w-5 h-5 text-muted-foreground/40 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p
                      className={cn(
                        'text-sm truncate',
                        taken ? 'text-muted-foreground line-through' : 'text-foreground'
                      )}
                      dir="auto"
                    >
                      {d.name}
                      {d.dosage && (
                        <span className="text-xs text-muted-foreground ms-1.5">({d.dosage})</span>
                      )}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground font-mono flex-shrink-0" dir="ltr">
                    {d.time}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      </CardContent>
    </Card>
  )
}

// Section renderer shared by Medications and Supplements — same UI
// for the list + add/edit/delete card, only the data source and
// translation prefix differ. `historyRows` + `todayIso` feed the
// adherence-streak badge on each item card.
function ScheduledItemsSection({ kind, icon: Icon, data, confirm, historyRows, todayIso }) {
  const { t } = useTranslation()
  const items = data.items
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const handleSave = async (values) => {
    setSubmitting(true)
    if (editing) await data.update(editing.id, values)
    else await data.create(values)
    setSubmitting(false)
    setAdding(false)
    setEditing(null)
  }

  const handleDelete = async (item) => {
    const ok = await confirm({
      title: t(`healthProfile.${kind}.confirmDelete`),
      description: item.name,
      confirmLabel: t('common.delete'),
      variant: 'destructive',
      icon: Trash2,
    })
    if (ok) await data.remove(item.id)
  }

  return (
    <section className="space-y-2 pt-2">
      <div className="flex items-center justify-between px-1">
        <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5" dir="auto">
          <Icon className="w-4 h-4 text-app-primary-500" />
          {t(`healthProfile.${kind}.title`)}
        </h2>
        {!adding && !editing && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="w-4 h-4 me-1" />
            {t(`healthProfile.${kind}.add`)}
          </Button>
        )}
      </div>

      {adding && (
        <ScheduledItemForm
          kind={kind}
          onSave={handleSave}
          onCancel={() => setAdding(false)}
          submitting={submitting}
        />
      )}

      {data.loading ? (
        <Skeleton className="h-16 w-full" />
      ) : items.length === 0 && !adding ? (
        <EmptyState icon={Icon} title={t(`healthProfile.${kind}.empty`)} />
      ) : (
        items.map((m) => (
          <React.Fragment key={m.id}>
            {editing?.id === m.id ? (
              <ScheduledItemForm
                kind={kind}
                initial={m}
                onSave={handleSave}
                onCancel={() => setEditing(null)}
                submitting={submitting}
              />
            ) : (
              <Card>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-foreground" dir="auto">{m.name}</p>
                        {m.schedule_times?.length > 0 && (() => {
                          const streak = computeAdherenceStreak({
                            item: m,
                            itemKind: kind,
                            intakeRows: historyRows,
                            todayIso,
                          })
                          return (
                            <span
                              className={cn(
                                'inline-flex items-center gap-1 text-[11px] font-mono',
                                streak > 0 ? 'text-app-primary-500' : 'text-muted-foreground/60',
                              )}
                              aria-label={t(`healthProfile.${kind}.streakA11y`, { n: streak })}
                              dir="ltr"
                            >
                              <Flame className="w-3 h-3" />
                              {t(`healthProfile.${kind}.streak`, { n: streak })}
                            </span>
                          )
                        })()}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5" dir="auto">
                        {[m.dosage, t(`healthProfile.${kind}.frequencies.${m.frequency}`)].filter(Boolean).join(' · ')}
                      </p>
                      {m.schedule_times?.length > 0 && (
                        <p className="text-[11px] text-muted-foreground mt-1 font-mono" dir="ltr">
                          {m.schedule_times.join(' · ')}
                        </p>
                      )}
                      {m.notes && (
                        <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap" dir="auto">{m.notes}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {m.reminders_enabled ? (
                        <Bell className="w-3.5 h-3.5 text-app-primary-500" aria-label={t(`healthProfile.${kind}.reminders`)} />
                      ) : (
                        <BellOff className="w-3.5 h-3.5 text-muted-foreground/50" />
                      )}
                      <button
                        type="button"
                        onClick={() => setEditing(m)}
                        aria-label={t('common.edit')}
                        className="p-1 text-muted-foreground hover:text-app-primary-500"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(m)}
                        aria-label={t('common.delete')}
                        className="p-1 text-muted-foreground hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </React.Fragment>
        ))
      )}
    </section>
  )
}

export function HealthProfilePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const goBack = () => navigate('/')
  const meds = useMedications()
  const supps = useSupplements()
  const conds = useHealthConditions()
  const intake = useIntakeLog()
  const history = useIntakeHistory({ days: 90 })
  const todayIso = useMemo(() => new Date().toLocaleDateString('en-CA'), [])
  const confirm = useConfirm()

  const handleRefresh = useCallback(
    () => performRefresh([meds.refetch, supps.refetch, conds.refetch, intake.refetch, history.refetch]),
    [meds.refetch, supps.refetch, conds.refetch, intake.refetch, history.refetch],
  )
  const [addingCond, setAddingCond] = useState(false)
  const [editingCond, setEditingCond] = useState(null)
  const [submittingCond, setSubmittingCond] = useState(false)

  const handleSaveCond = async (values) => {
    setSubmittingCond(true)
    if (editingCond) await conds.update(editingCond.id, values)
    else await conds.create(values)
    setSubmittingCond(false)
    setAddingCond(false)
    setEditingCond(null)
  }

  const handleDeleteCond = async (cond) => {
    const ok = await confirm({
      title: t('healthProfile.condition.confirmDelete'),
      description: cond.name,
      confirmLabel: t('common.delete'),
      variant: 'destructive',
      icon: Trash2,
    })
    if (ok) await conds.remove(cond.id)
  }

  const medsData = {
    items: meds.medications,
    loading: meds.loading,
    create: meds.create,
    update: meds.update,
    remove: meds.remove,
  }
  const suppsData = {
    items: supps.supplements,
    loading: supps.loading,
    create: supps.create,
    update: supps.update,
    remove: supps.remove,
  }

  return (
    <PullToRefresh onRefresh={handleRefresh}>
    <div className="space-y-4 content-fade pb-8">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={goBack}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-foreground flex items-center gap-2" dir="auto">
            <Stethoscope className="w-5 h-5 text-app-primary-500" />
            {t('healthProfile.title')}
          </h1>
        </div>
      </div>

      <TodayDoseChecklist
        medications={meds.medications}
        supplements={supps.supplements}
        intake={intake}
      />

      <ScheduledItemsSection kind="medication" icon={Pill} data={medsData} confirm={confirm} historyRows={history.rows} todayIso={todayIso} />
      <ScheduledItemsSection kind="supplement" icon={Sparkles} data={suppsData} confirm={confirm} historyRows={history.rows} todayIso={todayIso} />

      <VitalsSection />

      <section className="space-y-2 pt-2">
        <div className="flex items-center justify-between px-1">
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5" dir="auto">
            <Stethoscope className="w-4 h-4 text-app-primary-500" />
            {t('healthProfile.condition.title')}
          </h2>
          {!addingCond && !editingCond && (
            <Button size="sm" variant="outline" onClick={() => setAddingCond(true)}>
              <Plus className="w-4 h-4 me-1" />
              {t('healthProfile.condition.add')}
            </Button>
          )}
        </div>

        {addingCond && (
          <ConditionForm
            onSave={handleSaveCond}
            onCancel={() => setAddingCond(false)}
            submitting={submittingCond}
          />
        )}

        {conds.loading ? (
          <Skeleton className="h-16 w-full" />
        ) : conds.conditions.length === 0 && !addingCond ? (
          <EmptyState icon={Stethoscope} title={t('healthProfile.condition.empty')} />
        ) : (
          conds.conditions.map((c) => (
            <React.Fragment key={c.id}>
              {editingCond?.id === c.id ? (
                <ConditionForm
                  initial={c}
                  onSave={handleSaveCond}
                  onCancel={() => setEditingCond(null)}
                  submitting={submittingCond}
                />
              ) : (
                <Card>
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground" dir="auto">{c.name}</p>
                        {(c.diagnosed_at || c.doctor) && (
                          <p className="text-xs text-muted-foreground mt-0.5" dir="auto">
                            {[c.diagnosed_at, c.doctor].filter(Boolean).join(' · ')}
                          </p>
                        )}
                        {c.notes && (
                          <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap" dir="auto">{c.notes}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => setEditingCond(c)}
                          aria-label={t('common.edit')}
                          className="p-1 text-muted-foreground hover:text-app-primary-500"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteCond(c)}
                          aria-label={t('common.delete')}
                          className="p-1 text-muted-foreground hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </React.Fragment>
          ))
        )}
      </section>
    </div>
    </PullToRefresh>
  )
}
